import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  trackEvent, 
  trackConversionEvent, 
  trackMetaEvent 
} from '@/lib/analytics';

// Scroll depth milestones to track
const SCROLL_MILESTONES = [25, 50, 75, 90, 100];

/**
 * Hook for tracking scroll depth on the current page
 * Fires events at 25%, 50%, 75%, 90%, and 100% scroll depth
 */
export const useScrollDepthTracking = () => {
  const location = useLocation();
  const firedMilestones = useRef<Set<number>>(new Set());

  useEffect(() => {
    // Reset milestones on route change
    firedMilestones.current = new Set();
    
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      
      if (docHeight <= 0) return;
      
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);
      
      SCROLL_MILESTONES.forEach(milestone => {
        if (scrollPercent >= milestone && !firedMilestones.current.has(milestone)) {
          firedMilestones.current.add(milestone);
          
          trackEvent({
            category: 'engagement',
            action: 'scroll_depth',
            label: `${milestone}%`,
            value: milestone,
          });
          
          trackConversionEvent('bbq_scroll_depth', {
            milestone,
            page_path: location.pathname,
          });
          
          // Track 50% and 100% as Meta ViewContent events
          if (milestone === 50 || milestone === 100) {
            trackMetaEvent('ViewContent', {
              content_category: 'scroll_depth',
              content_name: `${milestone}% scroll`,
              value: milestone / 100,
            });
          }
        }
      });
    };

    // Throttle scroll handler for performance
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', throttledScroll);
    };
  }, [location.pathname]);
};

/**
 * Hook for tracking page views on route changes
 */
export const usePageViewTracking = () => {
  const location = useLocation();
  
  useEffect(() => {
    trackEvent({
      category: 'pageview',
      action: 'view',
      label: location.pathname,
    });
    
    trackConversionEvent('bbq_page_view', {
      page_path: location.pathname,
      page_search: location.search,
    });
    
    trackMetaEvent('PageView');
  }, [location.pathname, location.search]);
};

/**
 * Hook for tracking time on page
 * Fires events at 30s, 60s, 120s, and 300s
 */
export const useTimeOnPageTracking = () => {
  const location = useLocation();
  const firedIntervals = useRef<Set<number>>(new Set());
  
  useEffect(() => {
    firedIntervals.current = new Set();
    const startTime = Date.now();
    const intervals = [30, 60, 120, 300]; // seconds
    
    const checkTime = () => {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      
      intervals.forEach(interval => {
        if (elapsedSeconds >= interval && !firedIntervals.current.has(interval)) {
          firedIntervals.current.add(interval);
          
          trackEvent({
            category: 'engagement',
            action: 'time_on_page',
            label: `${interval}s`,
            value: interval,
          });
          
          trackConversionEvent('bbq_time_on_page', {
            seconds: interval,
            page_path: location.pathname,
          });
        }
      });
    };
    
    const intervalId = setInterval(checkTime, 5000);
    
    return () => clearInterval(intervalId);
  }, [location.pathname]);
};

/**
 * Track CTA button clicks with consistent naming
 */
export const useButtonClickTracking = () => {
  return useCallback((buttonName: string, buttonLocation: string, additionalData?: Record<string, unknown>) => {
    trackEvent({
      category: 'cta',
      action: 'button_click',
      label: `${buttonLocation}_${buttonName}`,
    });
    
    trackConversionEvent('bbq_button_click', {
      button_name: buttonName,
      button_location: buttonLocation,
      ...additionalData,
    });
  }, []);
};

/**
 * Track outbound link clicks
 */
export const useOutboundLinkTracking = () => {
  return useCallback((url: string, linkText?: string) => {
    trackEvent({
      category: 'outbound',
      action: 'link_click',
      label: url,
    });
    
    trackConversionEvent('bbq_outbound_link', {
      destination_url: url,
      link_text: linkText,
    });
  }, []);
};

/**
 * Track element visibility (for tracking when key sections are viewed)
 */
export const useVisibilityTracking = (
  elementRef: React.RefObject<HTMLElement>,
  elementName: string,
  options?: { threshold?: number; trackOnce?: boolean }
) => {
  const hasTracked = useRef(false);
  const { threshold = 0.5, trackOnce = true } = options || {};

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (trackOnce && hasTracked.current) return;
            hasTracked.current = true;

            trackEvent({
              category: 'visibility',
              action: 'section_view',
              label: elementName,
            });

            trackConversionEvent('bbq_section_view', {
              section_name: elementName,
            });
          }
        });
      },
      { threshold }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [elementRef, elementName, threshold, trackOnce]);
};

/**
 * Combined hook that sets up all automatic tracking
 */
export const useAnalyticsTracking = () => {
  usePageViewTracking();
  useScrollDepthTracking();
  useTimeOnPageTracking();
};
